import { IChatInputMessage, IChatMessageType, IThread } from '@fly/shared';
import { createSelector, nanoid } from '@reduxjs/toolkit';

import { apiSlice } from './apiSlice';
import { RootState } from './store';

const THREADS_PAGE_SIZE = 100;

interface ListThreadsParams {
  limit?: number;
}

export const chatService = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createThread: builder.mutation<IThread, void>({
      query: () => ({
        url: '/wingman/threads/',
        method: 'POST',
        config: {
          headers: { 'X-Fly-Client-Type': 'web' },
        },
      }),
      invalidatesTags: ['chat-threads'],
    }),
    listThreads: builder.infiniteQuery<
      IThread[],
      ListThreadsParams | void,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (
          lastPage,
          _allPages,
          _lastPageParam,
          allPageParams
        ) => {
          const lastOffset = allPageParams[allPageParams.length - 1] ?? 0;
          return lastPage.length >= THREADS_PAGE_SIZE
            ? lastOffset + lastPage.length
            : undefined;
        },
        getPreviousPageParam: () => undefined,
      },
      query: ({ queryArg, pageParam }) => {
        const limit = queryArg?.limit ?? THREADS_PAGE_SIZE;
        const searchParams = new URLSearchParams();
        searchParams.set('limit', String(limit));
        if (pageParam > 0) {
          searchParams.set('offset', String(pageParam));
        }
        return {
          url: `/wingman/threads/?${searchParams.toString()}`,
          method: 'GET',
          config: {
            headers: { 'X-Fly-Client-Type': 'web' },
          },
        };
      },
      providesTags: ['chat-threads'],
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        for (const thread of data.pages.flat()) {
          dispatch(
            chatService.util.upsertQueryData(
              'getThread',
              thread.thread_id,
              thread
            )
          );
        }
      },
    }),
    getThread: builder.query<IThread, string>({
      query: (threadId) => ({
        url: `/wingman/threads/${threadId}/`,
        method: 'GET',
        config: {
          headers: { 'X-Fly-Client-Type': 'web' },
        },
      }),
      providesTags: (_result, _error, threadId) => [
        { type: 'chat-threads', id: threadId },
      ],
    }),
    deleteThread: builder.mutation<void, string>({
      query: (threadId) => ({
        url: `/wingman/threads/${threadId}/`,
        method: 'DELETE',
        config: {
          headers: { 'X-Fly-Client-Type': 'web' },
        },
      }),
      async onQueryStarted(threadId, { dispatch, queryFulfilled }) {
        const listPatch = dispatch(
          chatService.util.updateQueryData(
            'listThreads',
            undefined,
            (draft) => {
              for (const page of draft.pages) {
                const idx = page.findIndex((t) => t.thread_id === threadId);
                if (idx !== -1) {
                  page.splice(idx, 1);
                  break;
                }
              }
            }
          )
        );
        try {
          await queryFulfilled;
          dispatch(
            chatService.util.invalidateTags([
              { type: 'chat-threads', id: threadId },
            ])
          );
        } catch {
          listPatch.undo();
        }
      },
    }),
    getThreadMessages: builder.query<IChatInputMessage[], string>({
      query: (threadId) => {
        return {
          url: `/wingman/threads/${threadId}/messages/`,
          method: 'GET',
          config: {
            headers: { 'X-Fly-Client-Type': 'web' },
          },
        };
      },
      transformResponse: (response: { messages: IChatInputMessage[] }) =>
        response.messages.map((message) => ({
          id: nanoid(),
          type: message.type,
          content: message.content,
        })),
    }),
  }),
});

export const selectThreadMessagesResult = (
  state: RootState,
  threadId: string
) => chatService.endpoints.getThreadMessages.select(threadId)(state);

export const selectThreadMessageIds = (state: RootState, threadId: string) =>
  selectThreadMessagesResult(state, threadId).data?.map((m) => m.id) ?? [];

export const selectThreadMessageById = (
  state: RootState,
  threadId: string,
  messageId: string
) =>
  selectThreadMessagesResult(state, threadId).data?.find(
    (m) => m.id === messageId
  ) ?? null;

export const selectLastHumanMessage = createSelector(
  [selectThreadMessagesResult, (_, threadId: string) => threadId],
  (messages): IChatInputMessage | null => {
    if (!messages.data) {
      return null;
    }

    for (let i = messages.data.length - 1; i >= 0; i--) {
      if (messages.data[i].type === IChatMessageType.HUMAN) {
        return messages.data[i];
      }
    }
    return null;
  }
);

export const {
  useCreateThreadMutation,
  useListThreadsInfiniteQuery,
  useGetThreadQuery,
  useDeleteThreadMutation,
  useGetThreadMessagesQuery,
  useLazyGetThreadMessagesQuery,
} = chatService;
